import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaGraduationCap, FaTrash } from 'react-icons/fa';
import { Select, TimePicker, message, Modal } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { publicEducationService } from '../../../services/publicEducationService';
import '../../SportsManagement/SportsManagementStyle.scss';

dayjs.extend(customParseFormat);

const dayOptions = [
    { value: 1, label: 'Pazartesi' },
    { value: 2, label: 'Salı' },
    { value: 3, label: 'Çarşamba' },
    { value: 4, label: 'Perşembe' },
    { value: 5, label: 'Cuma' },
    { value: 6, label: 'Cumartesi' },
    { value: 0, label: 'Pazar' }
];

const WEEKLY_BLOCK_COURSE_NAME = 'Halk Egitim Kursu';

const getDayLabel = (dayValue) =>
    dayOptions.find((day) => Number(day.value) === Number(dayValue))?.label || '-';

const initialBlockedSlotForm = {
    blockType: 'singleDate',
    blockedDate: new Date().toISOString().split('T')[0],
    selectedDays: [1],
    startTime: '',
    endTime: ''
};

const formatHourLabel = (hour) => {
    if (hour === null || hour === undefined || Number.isNaN(Number(hour))) return '-';
    // If it's a whole number, format it as HH:00, but if it has decimals (like 8.25), we should handle it.
    // However, our backend usually returns strings like "08:15" now. 
    // If 'hour' is numeric, we format it.
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const extractArrayData = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
};

const normalizeAvailableSports = (facility) => {
    const rawSports = facility?.availableSports ?? facility?.AvailableSports;
    if (!Array.isArray(rawSports)) return [];

    return rawSports
        .map((sport) => {
            const typeId = sport?.typeId ?? sport?.TypeId ?? sport?.sportTypeId ?? sport?.SportTypeId ?? sport?.SportTypeID;
            const typeName = sport?.typeName ?? sport?.TypeName ?? sport?.sportTypeName ?? sport?.SportTypeName ?? '';
            return { typeId, typeName };
        })
        .filter((sport) => sport.typeId !== null && sport.typeId !== undefined && !Number.isNaN(Number(sport.typeId)))
        .map((sport) => ({
            typeId: Number(sport.typeId),
            typeName: sport.typeName || String(sport.typeId)
        }));
};

const normalizeRegistration = (item) => ({
    registrationId: item?.registrationId ?? item?.RegistrationId ?? item?.applicationId ?? item?.ApplicationID,
    facilityId: item?.facilityId ?? item?.FacilityID ?? item?.FacilityId ?? null,
    facilityName: item?.facilityName ?? item?.FacilityName ?? '',
    specificFacilityName: item?.specificFacilityName ?? item?.SpecificFacilityName ?? '',
    regionName: item?.regionName ?? item?.RegionName ?? '',
    firstName: item?.firstName ?? item?.FirstName ?? '',
    lastName: item?.lastName ?? item?.LastName ?? '',
    identityNumber: item?.identityNumber ?? item?.IdentityNumber ?? '',
    gender: item?.gender ?? item?.Gender ?? '',
    birthDate: item?.birthDate ?? item?.BirthDate ?? null,
    courseName: item?.courseName ?? item?.CourseName ?? null,
    sportTypeName: item?.sportTypeName ?? item?.SportTypeName ?? null,
    preferredDate: item?.preferredDate ?? item?.PreferredDate ?? null,
    preferredStartHour: item?.preferredStartHour ?? item?.PreferredStartHour ?? null,
    preferredEndHour: item?.preferredEndHour ?? item?.PreferredEndHour ?? null,
    phone: item?.phone ?? item?.Phone ?? '',
    email: item?.email ?? item?.Email ?? '',
    createdDate: item?.createdDate ?? item?.CreatedDate ?? null
});

const normalizeBlockedSlot = (item) => ({
    sourceType: 'blocked',
    blockedSlotId: item?.blockedSlotId ?? item?.BlockedSlotID ?? item?.BlockedSlotId,
    facilityId: item?.facilityId ?? item?.FacilityID ?? item?.FacilityId ?? null,
    sportTypeId: item?.sportTypeId ?? item?.SportTypeID ?? item?.SportTypeId ?? null,
    facilityName: item?.facilityName ?? item?.FacilityName ?? '',
    specificFacilityName: item?.specificFacilityName ?? item?.SpecificFacilityName ?? '',
    regionName: item?.regionName ?? item?.RegionName ?? '',
    sportTypeName: item?.sportTypeName ?? item?.SportTypeName ?? '',
    blockedDate: item?.blockedDate ?? item?.BlockedDate ?? null,
    startTime: item?.startTime ?? item?.StartTime ?? null,
    endTime: item?.endTime ?? item?.EndTime ?? null,
    reason: item?.reason ?? item?.Reason ?? null,
    createdBy: item?.createdBy ?? item?.CreatedBy ?? null
});

const normalizeCourseScheduleAsBlockedSlot = (item) => ({
    sourceType: 'weekly',
    blockedSlotId: item?.courseScheduleId ?? item?.CourseScheduleID ?? item?.CourseScheduleId,
    courseScheduleId: item?.courseScheduleId ?? item?.CourseScheduleID ?? item?.CourseScheduleId,
    facilityId: item?.facilityId ?? item?.FacilityID ?? item?.FacilityId ?? null,
    sportTypeId: item?.sportTypeId ?? item?.SportTypeID ?? item?.SportTypeId ?? null,
    facilityName: item?.facilityName ?? item?.FacilityName ?? '',
    specificFacilityName: item?.specificFacilityName ?? item?.SpecificFacilityName ?? '',
    regionName: item?.regionName ?? item?.RegionName ?? '',
    sportTypeName: item?.sportTypeName ?? item?.SportTypeName ?? '',
    blockedDate: null,
    dayOfWeek: item?.dayOfWeek ?? item?.DayOfWeek ?? null,
    startTime: item?.startTime ?? item?.StartTime ?? null,
    endTime: item?.endTime ?? item?.EndTime ?? null,
    reason: item?.courseName ?? item?.CourseName ?? WEEKLY_BLOCK_COURSE_NAME,
    createdBy: item?.createdBy ?? item?.CreatedBy ?? null
});

const PublicEducationManagement = () => {
    const [facilities, setFacilities] = useState([]);
    const [selectedFacility, setSelectedFacility] = useState('all');
    const [selectedSportType, setSelectedSportType] = useState('');
    const [selectedAreaIndex, setSelectedAreaIndex] = useState('');
    const [registrations, setRegistrations] = useState([]);
    const [selectedRegistrationTab, setSelectedRegistrationTab] = useState('all');
    const [blockedSlots, setBlockedSlots] = useState([]);
    const [blockedSlotForm, setBlockedSlotForm] = useState(initialBlockedSlotForm);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState('');

    const handleWeeklyDayChange = (values) => {
        setBlockedSlotForm((prev) => ({
            ...prev,
            selectedDays: values
        }));
    };

    const selectedFacilityItem = useMemo(
        () => facilities.find((facility) => String(facility.facilityId) === String(selectedFacility)),
        [facilities, selectedFacility]
    );

    // Generate facility areas (courts/fields) based on facility type
    const facilityAreas = useMemo(() => {
        if (!selectedFacilityItem) return [];

        const facilityName = (selectedFacilityItem.facilityName || '').toLowerCase();
        const sports = selectedFacilityItem.availableSports || [];

        // Specific templates for known facilities
        if (facilityName.includes('rauf') && facilityName.includes('denkta')) {
            return [
                { index: 1, label: '1. Kort', sportTypeId: sports[0]?.typeId, sportTypeName: sports[0]?.typeName },
                { index: 2, label: '2. Kort', sportTypeId: sports[0]?.typeId, sportTypeName: sports[0]?.typeName },
                { index: 3, label: '3. Kort', sportTypeId: sports[0]?.typeId, sportTypeName: sports[0]?.typeName },
                { index: 4, label: '4. Kort', sportTypeId: sports[0]?.typeId, sportTypeName: sports[0]?.typeName }
            ];
        }

        if (facilityName.includes('karayilan')) {
            return [{ index: 1, label: 'Futbol Sahası', sportTypeId: sports[0]?.typeId, sportTypeName: sports[0]?.typeName }];
        }

        if (facilityName.includes('millet') && (facilityName.includes('park') || facilityName.includes('bahce'))) {
            return [
                { index: 1, label: 'Basketbol/Voleybol Sahası', sportTypeId: sports.find(s => s.typeName?.toLowerCase().includes('basketbol') || s.typeName?.toLowerCase().includes('voleybol'))?.typeId, sportTypeName: 'Basketbol/Voleybol' },
                { index: 2, label: 'Futbol Sahası', sportTypeId: sports.find(s => s.typeName?.toLowerCase().includes('futbol'))?.typeId, sportTypeName: 'Futbol' },
                { index: 3, label: 'Tenis Kortu', sportTypeId: sports.find(s => s.typeName?.toLowerCase().includes('tenis'))?.typeId, sportTypeName: 'Tenis' }
            ];
        }

        // Generic: create one area per sport type
        if (sports.length > 0) {
            return sports.map((sport, idx) => ({
                index: idx + 1,
                label: sport.typeName,
                sportTypeId: sport.typeId,
                sportTypeName: sport.typeName
            }));
        }

        return [];
    }, [selectedFacilityItem]);

    const selectedAreaLabel = useMemo(() => {
        if (!selectedAreaIndex) return '';
        const area = facilityAreas.find(a => String(a.index) === String(selectedAreaIndex));
        return area?.label || '';
    }, [facilityAreas, selectedAreaIndex]);

    const resolvedSportTypeId = useMemo(() => {
        const selectedArea = facilityAreas.find((area) => String(area.index) === String(selectedAreaIndex));
        const fallbackSportTypeId = selectedArea?.sportTypeId ?? selectedFacilityItem?.availableSports?.[0]?.typeId;
        const sportTypeIdValue = selectedSportType || fallbackSportTypeId;
        return sportTypeIdValue === null || sportTypeIdValue === undefined || Number.isNaN(Number(sportTypeIdValue))
            ? null
            : Number(sportTypeIdValue);
    }, [facilityAreas, selectedAreaIndex, selectedFacilityItem, selectedSportType]);



    const sortedBlockedSlots = useMemo(() => (
        [...blockedSlots].sort((a, b) => {
            const isWeeklyA = a?.sourceType === 'weekly';
            const isWeeklyB = b?.sourceType === 'weekly';

            if (isWeeklyA && isWeeklyB) {
                const dayDiff = Number(a?.dayOfWeek ?? 0) - Number(b?.dayOfWeek ?? 0);
                if (dayDiff !== 0) return dayDiff;
                return String(a?.startTime || '').localeCompare(String(b?.startTime || ''));
            }

            if (isWeeklyA) return 1;
            if (isWeeklyB) return -1;

            const dateA = a?.blockedDate ? new Date(a.blockedDate).getTime() : 0;
            const dateB = b?.blockedDate ? new Date(b.blockedDate).getTime() : 0;
            if (dateA !== dateB) return dateB - dateA;
            return String(b?.startTime || '').localeCompare(String(a?.startTime || ''));
        })
    ), [blockedSlots]);

    const filteredBlockedSlots = useMemo(() => {
        if (selectedFacility === 'all') return sortedBlockedSlots;
        return sortedBlockedSlots.filter((slot) => String(slot.facilityId) === String(selectedFacility));
    }, [selectedFacility, sortedBlockedSlots]);

    const facilityNameById = useMemo(() => (
        facilities.reduce((acc, facility) => {
            const fName = facility?.facilityName ?? facility?.FacilityName ?? '';
            const rName = facility?.regionName ?? facility?.RegionName ?? '';
            // Prioritize RegionName (Complex name) over Specific Facility Name
            acc[String(facility.facilityId ?? facility.FacilityId)] = rName || fName;
            return acc;
        }, {})
    ), [facilities]);

    const getRegistrationFacilityLabel = useCallback((registration) => (
        registration?.regionName
        || facilityNameById[String(registration?.facilityId)]
        || registration?.facilityName
        || 'Bilinmeyen Tesis'
    ), [facilityNameById]);

    const getRegistrationFacilityTabKey = useCallback((registration) => {
        const facilityId = registration?.facilityId;
        if (facilityId !== null && facilityId !== undefined && facilityId !== '') {
            return `facility-${facilityId}`;
        }
        return `name-${String(getRegistrationFacilityLabel(registration)).toLowerCase()}`;
    }, [getRegistrationFacilityLabel]);

    const registrationFacilityTabs = useMemo(() => {
        const tabsByKey = new Map();

        registrations.forEach((registration) => {
            const key = getRegistrationFacilityTabKey(registration);
            if (!tabsByKey.has(key)) {
                tabsByKey.set(key, {
                    key,
                    label: getRegistrationFacilityLabel(registration)
                });
            }
        });

        return Array.from(tabsByKey.values())
            .sort((a, b) => String(a.label).localeCompare(String(b.label), 'tr'));
    }, [registrations, getRegistrationFacilityLabel, getRegistrationFacilityTabKey]);

    const filteredRegistrations = useMemo(() => {
        if (selectedRegistrationTab === 'all') return registrations;
        return registrations.filter((registration) => getRegistrationFacilityTabKey(registration) === selectedRegistrationTab);
    }, [registrations, selectedRegistrationTab, getRegistrationFacilityTabKey]);

    const loadFacilities = useCallback(async () => {
        try {
            const response = await publicEducationService.getFacilities(true);
            const data = extractArrayData(response);

            // Group facilities by region to ensure facilityNameById uses complex names
            const grouped = {};
            data.forEach(facility => {
                const fName = facility?.facilityName ?? facility?.FacilityName ?? '';
                const rName = facility?.regionName ?? facility?.RegionName ?? fName;
                const rId = facility?.regionId ?? facility?.RegionId ?? rName;

                if (!grouped[rId]) {
                    grouped[rId] = {
                        facilityId: facility?.facilityId ?? facility?.FacilityId,
                        facilityName: rName,
                        regionId: rId,
                        availableSports: []
                    };
                }

                const sports = normalizeAvailableSports(facility);
                sports.forEach((sport) => {
                    const exists = grouped[rId].availableSports.some(
                        (existingSport) => String(existingSport.typeId) === String(sport.typeId)
                    );
                    if (!exists) {
                        grouped[rId].availableSports.push(sport);
                    }
                });
            });

            setFacilities(Object.values(grouped));
            setApiError('');
        } catch (error) {
            console.error('Public education facilities failed:', error);
            setFacilities([]);
            setApiError('Halk Eğitim endpointine ulaşılamadı (404).');
        }
    }, []);

    const loadRegistrations = useCallback(async () => {
        setLoading(true);
        try {
            const registrationRes = await publicEducationService.getRegistrations(null, null);
            const registrationData = extractArrayData(registrationRes).map(normalizeRegistration);
            setRegistrations(registrationData);
            setApiError('');
        } catch (error) {
            console.error('Public education registrations failed:', error);
            setRegistrations([]);
            setApiError('Halk Eğitim endpointine ulaşılamadı (404).');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAllBlockedSlots = useCallback(async () => {
        try {
            const [blockedRes, courseRes] = await Promise.all([
                publicEducationService.getBlockedSlots(null, null, null),
                publicEducationService.getCourseSchedules(null, null)
            ]);

            const blockedData = extractArrayData(blockedRes).map(normalizeBlockedSlot);
            const weeklyCourseData = extractArrayData(courseRes)
                .filter((course) => {
                    const courseName = String(course?.courseName ?? course?.CourseName ?? '').toLowerCase();
                    return courseName.includes('halk egitim kursu');
                })
                .map(normalizeCourseScheduleAsBlockedSlot);

            setBlockedSlots([...blockedData, ...weeklyCourseData]);
            setApiError('');
        } catch (error) {
            console.error('Public education blocked slots failed:', error);
            setBlockedSlots([]);
            setApiError('Halk Eğitim endpointine ulaşılamadı (404).');
        }
    }, []);

    useEffect(() => {
        loadFacilities();
    }, [loadFacilities]);

    useEffect(() => {
        loadRegistrations();
    }, [loadRegistrations]);

    useEffect(() => {
        loadAllBlockedSlots();
    }, [loadAllBlockedSlots]);

    useEffect(() => {
        if (selectedRegistrationTab === 'all') return;
        const hasSelectedTab = registrationFacilityTabs.some((tab) => tab.key === selectedRegistrationTab);
        if (!hasSelectedTab) {
            setSelectedRegistrationTab('all');
        }
    }, [registrationFacilityTabs, selectedRegistrationTab]);

    useEffect(() => {
        if (!selectedFacility && facilities.length > 0) {
            setSelectedFacility(String(facilities[0].facilityId));
        }
    }, [facilities, selectedFacility]);

    useEffect(() => {
        if (facilityAreas.length === 0) {
            setSelectedAreaIndex('');
            return;
        }

        if (!selectedAreaIndex || !facilityAreas.some((area) => String(area.index) === String(selectedAreaIndex))) {
            setSelectedAreaIndex(String(facilityAreas[0].index));
        }
    }, [facilityAreas, selectedAreaIndex]);



    useEffect(() => {
        if (!selectedAreaIndex) {
            setSelectedSportType('');
            return;
        }

        const selectedArea = facilityAreas.find((area) => String(area.index) === String(selectedAreaIndex));
        const fallbackSportTypeId = selectedArea?.sportTypeId ?? selectedFacilityItem?.availableSports?.[0]?.typeId;
        setSelectedSportType(
            fallbackSportTypeId === null || fallbackSportTypeId === undefined ? '' : String(fallbackSportTypeId)
        );
    }, [facilityAreas, selectedAreaIndex, selectedFacilityItem]);

    useEffect(() => {
        setBlockedSlotForm((prev) => ({
            ...prev,
            startTime: '',
            endTime: ''
        }));
    }, [selectedFacility, selectedAreaIndex]);

    const [currentPage, setCurrentPage] = useState(1);
    const [blockedCurrentPage, setBlockedCurrentPage] = useState(1);
    const itemsPerPage = 15;

    const paginatedRegistrations = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredRegistrations.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredRegistrations, currentPage]);

    const paginatedBlockedSlots = useMemo(() => {
        const startIndex = (blockedCurrentPage - 1) * itemsPerPage;
        return filteredBlockedSlots.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredBlockedSlots, blockedCurrentPage]);

    const totalPages = Math.ceil(filteredRegistrations.length / itemsPerPage);
    const blockedTotalPages = Math.ceil(filteredBlockedSlots.length / itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedRegistrationTab, registrations]);

    useEffect(() => {
        setBlockedCurrentPage(1);
    }, [selectedFacility, blockedSlots]);

    const handleCreateBlockedSlot = async (event) => {
        event.preventDefault();

        if (!selectedFacility || selectedFacility === 'all') {
            message.warning('Lütfen kapatma için bir tesis seçin.');
            return;
        }

        if (resolvedSportTypeId === null) {
            message.warning('Lütfen bir saha seçin.');
            return;
        }

        if (blockedSlotForm.startTime === '' || blockedSlotForm.endTime === '') {
            message.warning('Lütfen saat aralığını seçin.');
            return;
        }

        if (blockedSlotForm.blockType === 'singleDate' && !blockedSlotForm.blockedDate) {
            message.warning('Lütfen tarih seçin.');
            return;
        }

        if (blockedSlotForm.blockType === 'weekly' && (!blockedSlotForm.selectedDays || blockedSlotForm.selectedDays.length === 0)) {
            message.warning('Lütfen en az bir gün seçin.');
            return;
        }

        try {
            if (blockedSlotForm.blockType === 'weekly') {
                const selectedDays = blockedSlotForm.selectedDays || [];
                const createResults = await Promise.allSettled(
                    selectedDays.map((dayOfWeek) => publicEducationService.createCourseSchedule({
                        facilityId: Number(selectedFacility),
                        sportTypeId: resolvedSportTypeId,
                        courseName: 'Halk Egitim Kursu',
                        dayOfWeek: Number(dayOfWeek),
                        startTime: blockedSlotForm.startTime,
                        endTime: blockedSlotForm.endTime,
                        createdBy: 'Panel'
                    }))
                );

                const failedDays = createResults
                    .map((result, index) => (result.status === 'rejected' ? selectedDays[index] : null))
                    .filter((value) => value !== null)
                    .map((value) => dayOptions.find((day) => day.value === value)?.label || String(value));

                if (failedDays.length > 0) {
                    setApiError(`Bazı günler eklenemedi: ${failedDays.join(', ')}`);
                } else {
                    setApiError('');
                }
            } else {
                await publicEducationService.createBlockedSlot({
                    facilityId: Number(selectedFacility),
                    sportTypeId: resolvedSportTypeId,
                    blockedDate: blockedSlotForm.blockedDate,
                    startTime: blockedSlotForm.startTime,
                    endTime: blockedSlotForm.endTime,
                    reason: null,
                    createdBy: 'Panel'
                });
            }

            setBlockedSlotForm((prev) => ({
                ...prev,
                startTime: '',
                endTime: ''
            }));
            await loadAllBlockedSlots();
            if (blockedSlotForm.blockType !== 'weekly') {
                setApiError('');
            }
        } catch (error) {
            console.error('Create blocked slot failed:', error);
            setApiError('Saat bloklama kaydı oluşturulamadı.');
        }
    };

    return (
        <div className="sports-management-page">
            <style>{`
                .custom-days-select .ant-select-selector {
                    min-height: 45px !important;
                    border-radius: 10px !important;
                    border: 1px solid #cbd5e1 !important;
                    padding-top: 4px !important;
                    padding-bottom: 4px !important;
                }
                .custom-days-select.ant-select-multiple .ant-select-selection-item {
                    border-radius: 6px;
                }
                .custom-days-select .ant-select-selection-wrap {
                    position: relative;
                }
                .custom-days-select .ant-select-selection-placeholder {
                    position: absolute !important;
                    left: 0 !important;
                    right: 0 !important;
                    top: 50% !important;
                    transform: translateY(-50%) !important;
                    text-align: center !important;
                    color: #000000 !important;
                }
                .custom-days-select .ant-select-selection-search {
                    display: none !important;
                }
                
                /* Pagination styles */
                .pagination-wrapper {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-top: 24px;
                    gap: 8px;
                }
                .page-btn {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    border: 1px solid #e2e8f0;
                    background: #ffffff;
                    color: #64748b;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }
                .page-btn:hover:not(:disabled) {
                    border-color: #3b82f6;
                    color: #3b82f6;
                    background: #f0f9ff;
                }
                .page-btn.active {
                    background: #3b82f6;
                    color: #ffffff;
                    border-color: #3b82f6;
                }
                .page-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                @media (max-width: 768px) {
                    .premium-card .card-body {
                        padding: 1rem;
                    }
                    .page-header h1 {
                        font-size: 1.5rem;
                    }
                    .data-table th, .data-table td {
                        padding: 10px;
                        font-size: 0.85rem;
                    }
                    .pagination-wrapper {
                        flex-wrap: wrap;
                    }
                }
            `}</style>
            <div className="page-header">
                <h1><FaGraduationCap /> Halk Eğitim Yönetimi</h1>
            </div>

            <div className="premium-card">
                <div className="card-header">
                    <h3>Tesis ve Alan Seçimi</h3>
                    {selectedAreaLabel && <span style={{ color: '#718096', fontSize: '0.9rem' }}>{selectedAreaLabel}</span>}
                </div>
                <div className="card-body">
                    <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>İşlem Yapılacak Tesis</label>
                            <select value={selectedFacility} onChange={(event) => setSelectedFacility(event.target.value)}>
                                <option value="all">Genel</option>
                                {facilities.map((facility) => (
                                    <option key={facility.facilityId} value={facility.facilityId}>
                                        {facility.facilityName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>İşlem Yapılacak Alan / Kort / Saha</label>
                            <select
                                value={selectedAreaIndex}
                                onChange={(event) => setSelectedAreaIndex(event.target.value)}
                                disabled={!selectedFacility || facilityAreas.length === 0}
                            >
                                <option value="">Alan seçiniz</option>
                                {facilityAreas.map((area) => (
                                    <option key={area.index} value={area.index}>
                                        {area.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                </div>
            </div>

            {apiError && (
                <div style={{ marginTop: '1rem', padding: '12px 14px', border: '1px solid #fecaca', borderRadius: '10px', background: '#fef2f2', color: '#991b1b' }}>
                    {apiError}
                </div>
            )}

            <div className="premium-card">
                <div className="card-header">
                    <h3>Ders / Bloklama</h3>
                    <span style={{ color: '#718096', fontSize: '0.9rem' }}>
                        {selectedFacility === 'all'
                            ? 'Tüm tesis ve alanlar'
                            : facilities.find((facility) => String(facility.facilityId) === String(selectedFacility))?.facilityName || 'Tüm tesis ve alanlar'}
                    </span>
                </div>
                <div className="card-body">
                    <form
                        onSubmit={handleCreateBlockedSlot}
                        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', alignItems: 'end', marginBottom: '1.5rem' }}
                    >
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Kapatma Tipi</label>
                            <select
                                value={blockedSlotForm.blockType}
                                style={{ height: '45px', borderRadius: '10px', border: '1px solid #cbd5e1', padding: '0 14px' }}
                                onChange={(event) => {
                                    setBlockedSlotForm((prev) => ({
                                        ...prev,
                                        blockType: event.target.value,
                                        selectedDays: prev.selectedDays && prev.selectedDays.length > 0 ? prev.selectedDays : [1]
                                    }));
                                }}
                            >
                                <option value="singleDate">Günlük (Seçilen Tarih)</option>
                                <option value="weekly">Haftalık (Her Hafta)</option>
                            </select>
                        </div>
                        {blockedSlotForm.blockType === 'singleDate' ? (
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label>Tarih</label>
                                <input
                                    type="date"
                                    required
                                    value={blockedSlotForm.blockedDate}
                                    onChange={(event) => setBlockedSlotForm({ ...blockedSlotForm, blockedDate: event.target.value })}
                                />
                            </div>
                        ) : (
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label>Günler</label>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    showSearch={false}
                                    placeholder="Gün Seçiniz"
                                    value={blockedSlotForm.selectedDays}
                                    onChange={handleWeeklyDayChange}
                                    options={dayOptions}
                                    maxTagCount="responsive"
                                    style={{ width: '100%' }}
                                    className="custom-days-select"
                                    dropdownStyle={{ zIndex: 9999, borderRadius: '10px' }}
                                />
                            </div>
                        )}
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Başlangıç</label>
                            <TimePicker
                                value={blockedSlotForm.startTime ? dayjs(blockedSlotForm.startTime, 'HH:mm') : null}
                                format="HH:mm"
                                minuteStep={15}
                                showNow={false}
                                allowClear={false}
                                onChange={(time, timeString) => setBlockedSlotForm({ ...blockedSlotForm, startTime: timeString })}
                                style={{ width: '100%', height: '45px', borderRadius: '10px' }}
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Bitiş</label>
                            <TimePicker
                                value={blockedSlotForm.endTime ? dayjs(blockedSlotForm.endTime, 'HH:mm') : null}
                                format="HH:mm"
                                minuteStep={15}
                                showNow={false}
                                allowClear={false}
                                onChange={(time, timeString) => setBlockedSlotForm({ ...blockedSlotForm, endTime: timeString })}
                                style={{ width: '100%', height: '45px', borderRadius: '10px' }}
                            />
                        </div>
                        <button type="submit" className="btn-premium btn-success" style={{ height: '45px' }}>
                            <FaCalendarAlt /> {blockedSlotForm.blockType === 'weekly' ? 'Haftalık Kapat' : 'Saat Kapat'}
                        </button>
                    </form>

                    <div className="table-responsive">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Tesis</th>
                                    <th>Alan</th>
                                    <th>Tarih</th>
                                    <th>Saat Aralığı</th>
                                    <th>Neden</th>
                                    <th>Oluşturan</th>
                                    <th>İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedBlockedSlots.length === 0 ? (
                                    <tr>
                                        <td colSpan="7">Kayıt bulunamadı.</td>
                                    </tr>
                                ) : (
                                    paginatedBlockedSlots.map((slot) => (
                                        <tr key={slot.sourceType === 'weekly' ? `course-${slot.courseScheduleId}` : `blocked-${slot.blockedSlotId}`}>
                                            <td>{slot.regionName || facilityNameById[String(slot.facilityId)] || slot.facilityName || '-'}</td>
                                            <td>
                                                {slot.sportTypeName
                                                    || facilities.find((facility) => String(facility.facilityId) === String(slot.facilityId))
                                                        ?.availableSports
                                                        ?.find((sport) => String(sport.typeId) === String(slot.sportTypeId))
                                                        ?.typeName
                                                    || '-'}
                                            </td>
                                            <td>
                                                {slot.sourceType === 'weekly'
                                                    ? `Her ${getDayLabel(slot.dayOfWeek)}`
                                                    : (slot.blockedDate ? new Date(slot.blockedDate).toLocaleDateString('tr-TR') : '-')}
                                            </td>
                                            <td>{slot.startTime || '-'} - {slot.endTime === '24:00' ? '23:59' : (slot.endTime || '-')}</td>
                                            <td>{slot.reason || '-'}</td>
                                            <td>{slot.createdBy || '-'}</td>
                                            <td className="actions-cell">
                                                <button
                                                    type="button"
                                                    className="btn-premium btn-sm btn-outline"
                                                    onClick={() => {
                                                        Modal.confirm({
                                                            title: 'Onay',
                                                            content: 'Bu bloklama kaydını kaldırmak istediğinize emin misiniz?',
                                                            okText: 'Evet',
                                                            cancelText: 'Hayır',
                                                            onOk: async () => {
                                                                try {
                                                                    if (slot.sourceType === 'weekly') {
                                                                        await publicEducationService.deleteCourseSchedule(slot.courseScheduleId);
                                                                    } else {
                                                                        await publicEducationService.deleteBlockedSlot(slot.blockedSlotId);
                                                                    }
                                                                    message.success('Bloklama kaydı kaldırıldı.');
                                                                    await loadAllBlockedSlots();
                                                                } catch (error) {
                                                                    console.error('Delete blocked slot failed:', error);
                                                                    setApiError('Bloklama kaydı silinemedi.');
                                                                }
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <FaTrash /> Kaldır
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {blockedTotalPages > 1 && (
                        <div className="pagination-wrapper">
                            <button 
                                className="page-btn" 
                                type="button"
                                onClick={() => setBlockedCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={blockedCurrentPage === 1}
                            >
                                <FaChevronLeft />
                            </button>
                            
                            {[...Array(blockedTotalPages)].map((_, i) => {
                                const pageNum = i + 1;
                                const isVisible = pageNum === 1 || pageNum === blockedTotalPages || (pageNum >= blockedCurrentPage - 2 && pageNum <= blockedCurrentPage + 2);
                                
                                if (!isVisible) return null;
                                
                                return (
                                    <button 
                                        key={pageNum}
                                        type="button"
                                        className={`page-btn ${blockedCurrentPage === pageNum ? 'active' : ''}`}
                                        onClick={() => setBlockedCurrentPage(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}

                            <button 
                                className="page-btn" 
                                type="button"
                                onClick={() => setBlockedCurrentPage(prev => Math.min(prev + 1, blockedTotalPages))}
                                disabled={blockedCurrentPage === blockedTotalPages}
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="premium-card">
                <div className="card-header">
                    <h3>Halk Eğitim Kayıt Tablosu</h3>
                </div>
                <div className="card-body">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                        <button
                            type="button"
                            onClick={() => setSelectedRegistrationTab('all')}
                            className="btn-premium btn-sm"
                            style={{
                                border: selectedRegistrationTab === 'all' ? '1px solid #1d4ed8' : '1px solid #cbd5e1',
                                background: selectedRegistrationTab === 'all' ? '#eff6ff' : '#ffffff',
                                color: selectedRegistrationTab === 'all' ? '#1e40af' : '#1f2937'
                            }}
                        >
                            Tüm Tesisler
                        </button>
                        {registrationFacilityTabs.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setSelectedRegistrationTab(tab.key)}
                                className="btn-premium btn-sm"
                                style={{
                                    border: selectedRegistrationTab === tab.key ? '1px solid #1d4ed8' : '1px solid #cbd5e1',
                                    background: selectedRegistrationTab === tab.key ? '#eff6ff' : '#ffffff',
                                    color: selectedRegistrationTab === tab.key ? '#1e40af' : '#1f2937'
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    {loading ? (
                        <div className="loading-spinner">Yükleniyor...</div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Ad Soyad</th>
                                            <th>TC</th>
                                            <th>Cinsiyet</th>
                                            <th>Doğum Tarihi</th>
                                            <th>Tesis</th>
                                            <th>Telefon</th>
                                            <th>E-posta</th>
                                            <th>Kayıt Zamanı</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedRegistrations.length === 0 ? (
                                            <tr>
                                                <td colSpan="8">Kayıt bulunamadı.</td>
                                            </tr>
                                        ) : (
                                            paginatedRegistrations.map((registration) => (
                                                <tr key={registration.registrationId}>
                                                    <td style={{ fontWeight: '600' }}>{registration.firstName} {registration.lastName}</td>
                                                    <td>{registration.identityNumber}</td>
                                                    <td>{registration.gender === 'Female' ? 'Kadın' : 'Erkek'}</td>
                                                    <td>{registration.birthDate ? new Date(registration.birthDate).toLocaleDateString('tr-TR') : '-'}</td>
                                                    <td style={{ color: '#3b82f6', fontWeight: '500' }}>{getRegistrationFacilityLabel(registration)}</td>
                                                    <td>{registration.phone}</td>
                                                    <td>{registration.email || '-'}</td>
                                                    <td>
                                                        {registration.createdDate ? new Date(registration.createdDate).toLocaleDateString('tr-TR') : '-'}
                                                        <br />
                                                        <small style={{ color: '#64748b' }}>{registration.createdDate ? new Date(registration.createdDate).toLocaleTimeString('tr-TR') : '-'}</small>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="pagination-wrapper">
                                    <button 
                                        className="page-btn" 
                                        type="button"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    
                                    {[...Array(totalPages)].map((_, i) => {
                                        const pageNum = i + 1;
                                        const isVisible = pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 2 && pageNum <= currentPage + 2);
                                        
                                        if (!isVisible) return null;
                                        
                                        return (
                                            <button 
                                                key={pageNum}
                                                type="button"
                                                className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                                                onClick={() => setCurrentPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}

                                    <button 
                                        className="page-btn" 
                                        type="button"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <FaChevronRight />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PublicEducationManagement;